/**
 * ProductSpecsPage — Admin · Product Specs (Squad C, 2026-07-01)
 *
 * Self-serve editor for Sun Stellar's product technical-spec data. The spec
 * DATA lives in the DB (one JSON blob per family, mirroring SS_PRODUCT_SPECS);
 * edits flow into proposals via the resolver automatically. With no DB override
 * a family is served from the code constant (Default badge); a first save
 * creates the override (Edited badge); Reset deletes it back to the default.
 *
 * Layout (mirrors ProposalBuilderPage.tsx conventions: AURA CSS vars, no
 * Tailwind `primary`, inline styles):
 *   - Left rail: family list with Edited/Default badge + shape chip.
 *   - Centre: the typed editor chosen by family `shape`.
 *   - Right: live resolved spec-sheet preview (sample SKU → sandboxed iframe).
 *
 * Endpoints consumed (Squad B):
 *   GET    /api/v1/admin/product-specs
 *   GET    /api/v1/admin/product-specs/:familyKey
 *   PUT    /api/v1/admin/product-specs/:familyKey   { data }
 *   DELETE /api/v1/admin/product-specs/:familyKey
 *   POST   /api/v1/admin/product-specs/:familyKey/preview  { sku, data }
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Layers, RotateCcw, Save } from 'lucide-react';
import {
  listFamilies,
  getFamily,
  putFamily,
  resetFamily,
  type SpecFamily,
} from '../../api/productSpecs';
import { tokensForFamily } from './product-specs/tokens';
import { SpecFocusProvider } from './product-specs/specPrimitives';
import { TankVariantEditor } from './product-specs/TankVariantEditor';
import { HpModelEditor } from './product-specs/HpModelEditor';
import { SwhSeriesEditor } from './product-specs/SwhSeriesEditor';
import { FixedPageEditor } from './product-specs/FixedPageEditor';
import { DatasheetListEditor } from './product-specs/DatasheetListEditor';
import { PreviewPane } from './product-specs/PreviewPane';

// ─── Toast (mirrors ProposalBuilderPage) ──────────────────────────────────────

interface SaveToast {
  visible: boolean;
  msg: string;
}

function SaveToastBanner({ toast }: { toast: SaveToast }) {
  if (!toast.visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'var(--ink-1)',
        color: '#fff',
        borderRadius: 10,
        padding: '10px 18px',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 4px 16px rgba(0,0,0,.24)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      <Check size={14} />
      {toast.msg}
    </div>
  );
}

// ─── Badges ────────────────────────────────────────────────────────────────────

function EditedBadge({ edited }: { edited: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 18,
        padding: '0 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        background: edited ? 'rgba(201,138,59,.14)' : 'rgba(60,120,80,.10)',
        color: edited ? 'var(--saffron-600)' : 'var(--forest)',
        whiteSpace: 'nowrap',
      }}
    >
      {edited ? 'Edited' : 'Default'}
    </span>
  );
}

const SHAPE_LABEL: Record<string, string> = {
  tank_variant: 'Tank variants',
  hp_model: 'HP models',
  swh_series: 'SWH series',
  fixed_page: 'Fixed page',
  datasheet_list: 'Datasheets',
};

// ─── Editor dispatch by shape ──────────────────────────────────────────────────

function EditorForShape({
  familyKey,
  shape,
  data,
  onChange,
}: {
  familyKey: string;
  shape: string;
  data: any;
  onChange: (next: any) => void;
}) {
  const tokens = tokensForFamily(familyKey);
  switch (shape) {
    case 'tank_variant':
      return <TankVariantEditor data={data} tokens={tokens} onChange={onChange} />;
    case 'hp_model':
      return <HpModelEditor data={data} tokens={tokens} onChange={onChange} />;
    case 'swh_series':
      return <SwhSeriesEditor data={data} tokens={tokens} onChange={onChange} />;
    case 'fixed_page':
      return <FixedPageEditor data={data} tokens={tokens} familyKey={familyKey} onChange={onChange} />;
    case 'datasheet_list':
      return <DatasheetListEditor data={data} onChange={onChange} />;
    default:
      return (
        <div style={{ padding: 16, fontSize: 13, color: 'var(--ink-3)' }}>
          No editor for shape “{shape}”.
        </div>
      );
  }
}

// ─── Main page ──────────────────────────────────────────────────────────────────

export function ProductSpecsPage() {
  const [families, setFamilies] = useState<SpecFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [shape, setShape] = useState<string>('');
  const [draft, setDraft] = useState<any>(null);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<SaveToast>({ visible: false, msg: '' });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((msg = 'Saved') => {
    setToast({ visible: true, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast({ visible: false, msg: '' }), 2200);
  }, []);

  // Load the family list once; auto-select the first.
  const loadFamilies = useCallback(async (autoSelect = true) => {
    try {
      const list = await listFamilies();
      setFamilies(list);
      if (autoSelect && list.length && !selectedKey) setSelectedKey(list[0].key);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load families');
    } finally {
      setLoading(false);
    }
  }, [selectedKey]);

  useEffect(() => {
    loadFamilies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the selected family's data into the draft.
  useEffect(() => {
    if (!selectedKey) return;
    let cancelled = false;
    setFamilyLoading(true);
    setDirty(false);
    getFamily(selectedKey)
      .then((detail) => {
        if (cancelled) return;
        setShape(detail.shape);
        setDraft(detail.data ?? {});
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load family');
      })
      .finally(() => {
        if (!cancelled) setFamilyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKey]);

  const handleSelect = (key: string) => {
    if (key === selectedKey) return;
    if (dirty && !window.confirm('You have unsaved edits. Discard them and switch family?')) return;
    setSelectedKey(key);
  };

  const handleChange = (next: any) => {
    setDraft(next);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedKey || !dirty) return;
    setSaving(true);
    try {
      const res = await putFamily(selectedKey, draft);
      if (res?.data) setDraft(res.data);
      setDirty(false);
      // The family is now an override → flip the badge in the list.
      setFamilies((prev) => prev.map((f) => (f.key === selectedKey ? { ...f, edited: true } : f)));
      flash('Saved');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey) return;
    if (!window.confirm('Reset this family to the code default? Your DB override will be deleted.')) return;
    setSaving(true);
    try {
      await resetFamily(selectedKey);
      setFamilies((prev) => prev.map((f) => (f.key === selectedKey ? { ...f, edited: false } : f)));
      // Re-fetch the now-default data into the editor.
      const detail = await getFamily(selectedKey);
      setShape(detail.shape);
      setDraft(detail.data ?? {});
      setDirty(false);
      flash('Reset to default');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? e?.message ?? 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedFamily = families.find((f) => f.key === selectedKey) ?? null;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ink-3)' }}>
        Loading…
      </div>
    );
  }

  if (error && !families.length) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <p style={{ color: '#b91c1c', fontSize: 14 }}>{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            loadFamilies();
          }}
          style={{
            height: 34,
            padding: '0 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--ivory-0)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--ivory-2)',
        position: 'relative',
      }}
    >
      {/* Page header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-0)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--ink-1)' }}>
            Product Specs
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--ink-3)' }}>
            Edit the technical-spec data that flows into proposals — KPI tiles, spec rows,
            variants, models, datasheets.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleReset}
            disabled={saving || !selectedFamily?.edited}
            title={selectedFamily?.edited ? 'Delete the DB override and revert to the code default' : 'Already on the default'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: '1px solid var(--line)',
              background: 'var(--ivory-0)',
              color: selectedFamily?.edited ? 'var(--ink-2)' : 'var(--ink-3)',
              cursor: selectedFamily?.edited && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            <RotateCcw size={14} /> Reset to default
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 16px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              background: dirty ? 'var(--ink-1)' : 'var(--ivory-2)',
              color: dirty ? 'var(--ivory-0)' : 'var(--ink-3)',
              cursor: dirty && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Body: family rail + editor + preview */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Left rail */}
        <div
          style={{
            width: 256,
            flexShrink: 0,
            borderRight: '1px solid var(--line)',
            background: 'var(--ivory-0)',
            overflowY: 'auto',
            padding: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              padding: '4px 6px 8px',
            }}
          >
            Families
          </div>
          {families.map((f) => {
            const on = f.key === selectedKey;
            return (
              <button
                key={f.key}
                onClick={() => handleSelect(f.key)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 10px',
                  marginBottom: 4,
                  borderRadius: 9,
                  border: `1px solid ${on ? 'var(--saffron)' : 'var(--line)'}`,
                  background: on ? 'var(--ivory-2)' : 'var(--ivory-0)',
                  boxShadow: on ? '0 0 0 2px rgba(201,138,59,.14)' : 'none',
                  cursor: 'pointer',
                  transition: 'box-shadow .12s, border-color .12s',
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink-1)',
                    marginBottom: 5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {f.displayName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <EditedBadge edited={f.edited} />
                  <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                    {SHAPE_LABEL[f.shape] ?? f.shape} · {f.modelCount}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Centre: editor */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            background: 'var(--ivory-0)',
            borderRight: '1px solid var(--line)',
          }}
        >
          {familyLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              Loading {selectedFamily?.displayName ?? 'family'}…
            </div>
          ) : !selectedFamily || draft == null ? (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: 'var(--ink-3)',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: 'var(--ivory-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                  color: 'var(--ink-3)',
                }}
              >
                <Layers size={22} />
              </div>
              Select a family to edit its spec data.
            </div>
          ) : (
            <div style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink-1)' }}>
                    {selectedFamily.displayName}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <EditedBadge edited={selectedFamily.edited} />
                    {dirty && (
                      <span style={{ fontSize: 11, color: 'var(--saffron-600)', fontWeight: 600 }}>
                        unsaved changes
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* SpecFocusProvider scopes the placeholder palette to this editor. */}
              <SpecFocusProvider>
                <EditorForShape
                  familyKey={selectedKey}
                  shape={shape}
                  data={draft}
                  onChange={handleChange}
                />
              </SpecFocusProvider>
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div style={{ width: 440, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {selectedFamily && draft != null && !familyLoading ? (
            <PreviewPane familyKey={selectedKey} shape={shape as any} data={draft} />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-3)',
                fontSize: 12.5,
                background: 'var(--ivory-2)',
              }}
            >
              Preview appears here.
            </div>
          )}
        </div>
      </div>

      <SaveToastBanner toast={toast} />
    </div>
  );
}
